// src/pages/CompanyManagement/CompanyDetails.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import OverviewTab from './tabs/OverviewTab';
import SettingsTab from './tabs/SettingsTab';
import SupportTypesTab from './tabs/SupportTypesTab';
import ServicesTab from './tabs/ServicesTab';
import AdvancedTab from './tabs/AdvancedTab';
import UsersTab from './tabs/UsersTab';
import LocationsTab from './tabs/LocationsTab';

const CompanyDetails = ({ company, onUpdate, onDelete, t }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState(company);
  const [hasChanges, setHasChanges] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  useEffect(() => {
    setCompanyData(company);
    setHasChanges(false);
  }, [company]);

  const handleDataChange = (updates) => {
    setCompanyData(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(
        `${API_URL}/api/companies/${company.id}`,
        companyData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        enqueueSnackbar(t('CompanyManagement.success_updated'), { variant: 'success' });
        setHasChanges(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating company:', error);
      enqueueSnackbar(
        error.response?.data?.error || t('CompanyManagement.error_update'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('CompanyManagement.delete_confirm', { name: company.name }))) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.delete(
        `${API_URL}/api/companies/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      enqueueSnackbar(
        error.response?.data?.error || t('CompanyManagement.error_delete'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography variant="h6">{company.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('CompanyManagement.tenant_id_label')} {company.tenantId}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={loading}
          >
            {t('CompanyManagement.delete')}
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || loading}
          >
            {t('CompanyManagement.save_changes')}
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={t('CompanyManagement.tab_overview')} />
          <Tab label={t('CompanyManagement.tab_settings')} />
          <Tab label={t('CompanyManagement.tab_support_types')} />
          <Tab label={t('CompanyManagement.tab_services') || 'Services'} />
          <Tab label={t('CompanyManagement.tab_advanced')} />
          <Tab label={t('CompanyManagement.tab_users')} />
          <Tab label={t('CompanyManagement.tab_locations')} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {activeTab === 0 && <OverviewTab company={companyData} t={t} />}
        {activeTab === 1 && (
          <SettingsTab
            company={companyData}
            onChange={handleDataChange}
            t={t}
          />
        )}
        {activeTab === 2 && (
          <SupportTypesTab
            company={companyData}
            onChange={handleDataChange}
            t={t}
          />
        )}
        {activeTab === 3 && (
          <ServicesTab
            company={companyData}
            onChange={handleDataChange}
            t={t}
          />
        )}
        {activeTab === 4 && (
          <AdvancedTab
            company={companyData}
            onChange={handleDataChange}
            t={t}
          />
        )}
        {activeTab === 5 && <UsersTab companyId={company.id} t={t} />}
        {activeTab === 6 && <LocationsTab companyId={company.id} t={t} />}
      </Box>
    </Box>
  );
};

export default CompanyDetails;
